import { Button, ButtonDesign, MessageBoxTypes } from '@ui5/webcomponents-react';
import { ConfirmationDialog } from 'components/shared/ConfirmationDialog';
import { ResultUserMessage } from 'components/shared/UserMessage';
import {
  BaseQueryDetailsFragment,
  ClarifyQueryInput,
  ResourceAccessLevel,
  useClarifyQueryMutation
} from 'generated/graphql';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './ClarifyQuery.jss';

type ClarifyQueryProps = {
  resourceAccessLevel?: ResourceAccessLevel;
  query?: Pick<BaseQueryDetailsFragment, 'id' | 'createdBy'> | null;
  setResultMessage: (value: ResultUserMessage) => void;
  setIsUserMessageOpen: (value: boolean) => void;
};

export const ClarifyQuery: React.FC<ClarifyQueryProps> = (props) => {
  const { query, resourceAccessLevel, setIsUserMessageOpen, setResultMessage } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [isClarifyConfirmMessageBoxOpen, setIsClarifyConfirmMessageBoxOpen] = useState(false);
  const [, clarifyQuery] = useClarifyQueryMutation();

  const hasPermissionToClarifyQuery = () => {
    if (query?.createdBy.internal === undefined || resourceAccessLevel === undefined) {
      return false;
    }

    const internalUserAllowed = resourceAccessLevel === ResourceAccessLevel.INTERNAL;
    const externalUserAllowed =
      resourceAccessLevel === ResourceAccessLevel.SAME_AGENCY && !query.createdBy.internal;
    return externalUserAllowed || internalUserAllowed;
  };

  const confirmClarifyQuery = async () => {
    const comment: ClarifyQueryInput = {
      commentText: t('display-query-clarify-comment-message'),
      queryId: parseInt(query?.id ?? '')
    };
    const { error } = await clarifyQuery({ input: comment });
    if (error) {
      setResultMessage({
        isSuccess: false,
        message: t('display-query-clarify-error')
      });
    } else {
      setResultMessage({
        isSuccess: true,
        message: t('display-query-clarify-success')
      });
    }
    setIsUserMessageOpen(true);
  };

  return hasPermissionToClarifyQuery() ? (
    <>
      <Button
        className={classes.actionGroupItem}
        data-testid="clarify-query-item"
        data-help-id="clarify-query-item"
        id="clarify-query-button"
        onClick={() => setIsClarifyConfirmMessageBoxOpen(true)}
        design={ButtonDesign.Default}
      >
        {t('display-query-clarify-query-button')}
      </Button>
      <ConfirmationDialog
        data-testid="display-query-clarify-confirm-message"
        open={isClarifyConfirmMessageBoxOpen}
        type={MessageBoxTypes.Warning}
        onClose={() => setIsClarifyConfirmMessageBoxOpen(false)}
        onConfirm={confirmClarifyQuery}
      >
        {t('display-query-clarify-confirm-message')}
      </ConfirmationDialog>
    </>
  ) : null;
};
